import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  buildNamedNotificationRoute,
  listMyNamedPayrollNotifications,
  markNamedPayrollNotificationRead,
  type NamedPayrollNotification,
} from "@/services/payroll/namedRectifier";

const labels: Record<string, string> = {
  PAYROLL_BLOCKER_ASSIGNED: "Payroll repair assigned",
  PAYROLL_BLOCKER_RESOLVED_BY_OTHER: "Payroll repair resolved",
  PAYROLL_ISSUER_REVIEW_READY: "Payroll ready for review",
  PAYROLL_BLOCKER_REASSIGNMENT_REQUIRED: "Payroll repair needs reassignment",
};

export default function PayrollNotificationsButton({ companyId }: { companyId?: string }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NamedPayrollNotification[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!companyId) return;
    try {
      setItems(await listMyNamedPayrollNotifications(20));
      setError(null);
    } catch (caught) {
      setError((caught as Error).message ?? "Notifications are unavailable.");
    }
  }, [companyId]);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), 60_000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const unread = useMemo(() => items.filter((item) => !item.read_at).length, [items]);

  async function openNotification(item: NamedPayrollNotification) {
    const route = buildNamedNotificationRoute(item);
    if (!item.read_at) {
      await markNamedPayrollNotificationRead(item.notification_id);
    }
    setOpen(false);
    await refresh();
    if (route) navigate(route);
  }

  return (
    <div className="relative">
      <button
        type="button"
        aria-label={`Payroll notifications${unread ? `, ${unread} unread` : ""}`}
        onClick={() => setOpen((value) => !value)}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 ? (
          <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-red-600 px-1 text-center text-[10px] font-black leading-5 text-white">
            {Math.min(unread, 99)}
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="absolute right-0 top-11 z-50 w-[min(24rem,calc(100vw-2rem))] rounded-2xl border border-slate-200 bg-white p-3 shadow-xl dark:border-slate-800 dark:bg-slate-950">
          <div className="flex items-center justify-between px-2 py-1">
            <p className="text-xs font-black uppercase tracking-wider text-slate-700 dark:text-slate-200">Payroll work</p>
            <button type="button" onClick={() => void refresh()} className="text-xs font-semibold text-brand-vault">Refresh</button>
          </div>
          {error ? <p className="p-3 text-sm text-red-700">{error}</p> : null}
          {!error && items.length === 0 ? <p className="p-3 text-sm text-slate-500">No payroll notifications.</p> : null}
          <div className="mt-2 max-h-80 space-y-1 overflow-y-auto">
            {items.map((item) => (
              <button
                key={item.notification_id}
                type="button"
                onClick={() => void openNotification(item)}
                className={`w-full rounded-xl p-3 text-left transition hover:bg-slate-100 dark:hover:bg-slate-900 ${item.read_at ? "opacity-70" : "bg-brand-accent/5"}`}
              >
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{labels[item.kind] ?? "Payroll update"}</p>
                <p className="mt-1 text-xs text-slate-500">{new Date(item.created_at).toLocaleString()}</p>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

